import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Tooltip,
  FormControlLabel,
  Switch,
  Chip,
  FormHelperText,
} from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material'
import { informationSourcesAPI, contentGroupsAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'

const AUDIENCE_OPTIONS = [
  { value: 'patient', label: 'Patient' },
  { value: 'professional', label: 'Professional' },
]

// Normalize list responses to an array regardless of envelope shape
const toList = (data, key) => {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.[key])) return data[key]
  if (Array.isArray(data?.data)) return data.data
  return []
}

const arrayToCsv = (arr) => (Array.isArray(arr) ? arr.join(', ') : '')
const csvToArray = (str) =>
  (str || '')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean)

const EMPTY_FORM = {
  group_id: '',
  code: '',
  name: '',
  org_type: '',
  audience: 'patient',
  url: '',
  description: '',
  topics: '',
  bc_specific: false,
  bc_subtypes: '',
  scope: '',
  update_cadence_note: '',
  mobile_app_note: '',
  priority_rank: '',
  patient_counterpart_id: '',
  is_active: true,
}

function InformationSources() {
  const { hasRole } = useAuth()
  const canEdit = hasRole(['system_admin', 'administrator', 'clinical_designer'])
  const [sources, setSources] = useState([])
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Filter state
  const [searchTerm, setSearchTerm] = useState('')
  const [filterGroup, setFilterGroup] = useState('')
  const [showInactive, setShowInactive] = useState(false)

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingSource, setEditingSource] = useState(null)
  const [formData, setFormData] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)
      const [sourcesResponse, groupsResponse] = await Promise.all([
        informationSourcesAPI.getAll({ include_inactive: true }),
        contentGroupsAPI.getAll(true),
      ])
      setSources(toList(sourcesResponse.data, 'information_sources'))
      setGroups(toList(groupsResponse.data, 'content_groups'))
    } catch (err) {
      console.error('Error fetching information sources:', err)
      setError('Failed to load information sources. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const groupName = (groupId) => groups.find((g) => g.id === groupId)?.name || '—'

  const filteredSources = sources.filter((source) => {
    const matchesSearch =
      !searchTerm ||
      source.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      source.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (source.topics || []).some((t) => t.toLowerCase().includes(searchTerm.toLowerCase()))
    const matchesGroup = !filterGroup || source.group_id === filterGroup
    const matchesActive = showInactive || source.is_active !== false
    return matchesSearch && matchesGroup && matchesActive
  })

  // Patient-audience sources available as counterparts for professional sources
  const patientSources = sources.filter((s) => s.audience === 'patient')

  const handleOpenDialog = (source = null) => {
    if (source) {
      setEditingSource(source)
      setFormData({
        group_id: source.group_id || '',
        code: source.code || '',
        name: source.name || '',
        org_type: source.org_type || '',
        audience: source.audience || 'patient',
        url: source.url || '',
        description: source.description || '',
        topics: arrayToCsv(source.topics),
        bc_specific: !!source.bc_specific,
        bc_subtypes: arrayToCsv(source.bc_subtypes),
        scope: source.scope || '',
        update_cadence_note: source.update_cadence_note || '',
        mobile_app_note: source.mobile_app_note || '',
        priority_rank: source.priority_rank ?? '',
        patient_counterpart_id: source.patient_counterpart_id || '',
        is_active: source.is_active !== false,
      })
    } else {
      setEditingSource(null)
      setFormData(EMPTY_FORM)
    }
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setEditingSource(null)
    setFormData(EMPTY_FORM)
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)
      const payload = {
        group_id: formData.group_id || null,
        code: formData.code,
        name: formData.name,
        org_type: formData.org_type || null,
        audience: formData.audience,
        url: formData.url || null,
        description: formData.description || null,
        topics: csvToArray(formData.topics),
        bc_specific: formData.bc_specific,
        bc_subtypes: csvToArray(formData.bc_subtypes),
        scope: formData.scope || null,
        update_cadence_note: formData.update_cadence_note || null,
        mobile_app_note: formData.mobile_app_note || null,
        priority_rank: formData.priority_rank === '' ? null : Number(formData.priority_rank),
        patient_counterpart_id: formData.patient_counterpart_id || null,
        is_active: formData.is_active,
      }
      if (editingSource) {
        await informationSourcesAPI.update(editingSource.id, payload)
      } else {
        await informationSourcesAPI.create(payload)
      }
      handleCloseDialog()
      fetchData()
    } catch (err) {
      console.error('Error saving information source:', err)
      setError(err.response?.data?.message || 'Failed to save information source. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const updateField = (field) => (e) => setFormData({ ...formData, [field]: e.target.value })

  const columns = [
    {
      field: 'name',
      headerName: 'Source',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {params.row.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" fontFamily="monospace" fontSize="0.6875rem">
            {params.row.code}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'audience',
      headerName: 'Audience',
      width: 110,
      renderCell: (params) => (
        <Chip
          label={AUDIENCE_OPTIONS.find((a) => a.value === params.value)?.label || params.value || '—'}
          size="small"
          color={params.value === 'patient' ? 'primary' : 'default'}
          variant={params.value === 'patient' ? 'filled' : 'outlined'}
          sx={{ fontSize: '0.6875rem', height: 20 }}
        />
      ),
    },
    {
      field: 'bc_specific',
      headerName: 'BC-Specific',
      width: 95,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography variant="body2" fontSize="0.8125rem" color={params.value ? 'text.primary' : 'text.secondary'}>
          {params.value ? 'Yes' : '—'}
        </Typography>
      ),
    },
    {
      field: 'topics',
      headerName: 'Topics',
      flex: 1,
      minWidth: 180,
      sortable: false,
      renderCell: (params) => {
        const topics = params.value || []
        return (
          <Box display="flex" gap={0.5} flexWrap="wrap" alignItems="center">
            {topics.slice(0, 3).map((topic) => (
              <Chip key={topic} label={topic} size="small" sx={{ fontSize: '0.6875rem', height: 20 }} />
            ))}
            {topics.length > 3 && (
              <Typography variant="caption" color="text.secondary">
                +{topics.length - 3}
              </Typography>
            )}
          </Box>
        )
      },
    },
    {
      field: 'priority_rank',
      headerName: 'Priority',
      width: 75,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography variant="body2" fontSize="0.8125rem">
          {params.value ?? '—'}
        </Typography>
      ),
    },
    {
      field: 'group_id',
      headerName: 'Group',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary" fontSize="0.8125rem">
          {groupName(params.value)}
        </Typography>
      ),
    },
    {
      field: 'is_active',
      headerName: 'Active',
      width: 80,
      renderCell: (params) => (
        <Typography
          variant="body2"
          fontSize="0.8125rem"
          sx={{ color: params.value ? '#16a34a' : '#dc2626' }}
        >
          {params.value ? 'Active' : 'Inactive'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title={canEdit ? 'Edit' : 'View'}>
          <IconButton size="small" onClick={() => handleOpenDialog(params.row)}>
            {canEdit ? <EditIcon fontSize="small" /> : <ViewIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      ),
    },
  ]

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h5">Information Sources</Typography>
          <Typography variant="body2" color="text.secondary">
            Curated organizations and references used to answer patient information needs.
          </Typography>
        </Box>
        {canEdit && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            New Source
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <TextField
            placeholder="Search sources..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ width: 220 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Group</InputLabel>
            <Select value={filterGroup} label="Group" onChange={(e) => setFilterGroup(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {groups.map((group) => (
                <MenuItem key={group.id} value={group.id}>
                  {group.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
            }
            label={<Typography variant="body2">Show inactive</Typography>}
          />
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            {filteredSources.length} of {sources.length} sources
          </Typography>
        </Box>
      </Paper>

      {/* Source List */}
      <Paper>
        <DataGrid
          rows={filteredSources}
          columns={columns}
          pageSize={15}
          rowsPerPageOptions={[15, 25, 50]}
          autoHeight
          disableSelectionOnClick
          getRowId={(row) => row.id}
          onRowDoubleClick={(params) => handleOpenDialog(params.row)}
        />
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingSource ? (canEdit ? 'Edit Information Source' : 'View Information Source') : 'Create Information Source'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Box display="flex" gap={2}>
              <TextField
                fullWidth
                label="Code"
                value={formData.code}
                onChange={updateField('code')}
                margin="normal"
                required
                disabled={!canEdit}
                inputProps={{ style: { fontFamily: 'monospace' } }}
              />
              <FormControl fullWidth margin="normal" disabled={!canEdit}>
                <InputLabel>Group</InputLabel>
                <Select value={formData.group_id} label="Group" onChange={updateField('group_id')}>
                  <MenuItem value="">None</MenuItem>
                  {groups.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box display="flex" gap={2}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={updateField('name')}
                margin="normal"
                required
                disabled={!canEdit}
              />
              <TextField
                fullWidth
                label="Organization Type"
                value={formData.org_type}
                onChange={updateField('org_type')}
                margin="normal"
                disabled={!canEdit}
                helperText="e.g., government, nonprofit, professional society"
              />
            </Box>
            <Box display="flex" gap={2}>
              <FormControl fullWidth margin="normal" disabled={!canEdit}>
                <InputLabel>Audience</InputLabel>
                <Select value={formData.audience} label="Audience" onChange={updateField('audience')}>
                  {AUDIENCE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Professional-audience sources are never shown to patients.
                </FormHelperText>
              </FormControl>
              <TextField
                fullWidth
                label="Priority Rank"
                type="number"
                value={formData.priority_rank}
                onChange={updateField('priority_rank')}
                margin="normal"
                disabled={!canEdit}
                helperText="Lower rank = higher priority"
              />
            </Box>
            <TextField
              fullWidth
              label="URL"
              value={formData.url}
              onChange={updateField('url')}
              margin="normal"
              disabled={!canEdit}
            />
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={updateField('description')}
              margin="normal"
              multiline
              rows={3}
              disabled={!canEdit}
            />
            <TextField
              fullWidth
              label="Topics"
              value={formData.topics}
              onChange={updateField('topics')}
              margin="normal"
              disabled={!canEdit}
              helperText="Comma-separated (e.g., treatment, side_effects, clinical_trials)"
            />
            <Box display="flex" gap={2} alignItems="center">
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.bc_specific}
                    onChange={(e) => setFormData({ ...formData, bc_specific: e.target.checked })}
                    disabled={!canEdit}
                  />
                }
                label="Breast cancer specific"
                sx={{ whiteSpace: 'nowrap' }}
              />
              <TextField
                fullWidth
                label="BC Subtypes"
                value={formData.bc_subtypes}
                onChange={updateField('bc_subtypes')}
                margin="normal"
                disabled={!canEdit}
                helperText="Comma-separated (e.g., her2_positive, triple_negative)"
              />
            </Box>
            <TextField
              fullWidth
              label="Scope"
              value={formData.scope}
              onChange={updateField('scope')}
              margin="normal"
              multiline
              rows={2}
              disabled={!canEdit}
              helperText="What this source does and does not cover"
            />
            <Box display="flex" gap={2}>
              <TextField
                fullWidth
                label="Update Cadence Note"
                value={formData.update_cadence_note}
                onChange={updateField('update_cadence_note')}
                margin="normal"
                disabled={!canEdit}
              />
              <TextField
                fullWidth
                label="Mobile App Note"
                value={formData.mobile_app_note}
                onChange={updateField('mobile_app_note')}
                margin="normal"
                disabled={!canEdit}
              />
            </Box>
            <FormControl fullWidth margin="normal" disabled={!canEdit}>
              <InputLabel>Patient Counterpart</InputLabel>
              <Select
                value={formData.patient_counterpart_id}
                label="Patient Counterpart"
                onChange={updateField('patient_counterpart_id')}
              >
                <MenuItem value="">None</MenuItem>
                {patientSources
                  .filter((s) => !editingSource || s.id !== editingSource.id)
                  .map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
              </Select>
              <FormHelperText>
                Patient-facing source to surface instead of this professional source
              </FormHelperText>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  disabled={!canEdit}
                />
              }
              label="Active"
              sx={{ mt: 1 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{canEdit ? 'Cancel' : 'Close'}</Button>
          {canEdit && (
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={saving || !formData.code || !formData.name}
            >
              {saving ? 'Saving...' : editingSource ? 'Update' : 'Create'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default InformationSources
